import { PropertyValueMap } from 'lit'
import { LitElement } from 'lit'
import { property } from 'lit/decorators.js'

import { EventOptions } from './event'

export class DdsElement extends LitElement {
  /**
   * Accessible label for the element. Use this to provide a text alternative
   * for elements that don't have visible text content.
   * @group Accessibility
   */
  @property({ type: String, attribute: 'aria-label' })
  override ariaLabel: string | null = null

  /**
   * Indicates whether the element is expanded or collapsed.
   * @type { 'true' | 'false' }
   * @group Accessibility
   */
  @property({ type: String, attribute: 'aria-expanded' })
  override ariaExpanded: string | null = null

  /**
   * Identifies the element that is controlled by this element.
   * @group Accessibility
   */
  @property({ type: String, attribute: 'aria-controls' })
  ariaControls: string | null = null

  /**
   * Indicates the availability and type of interactive popup element.
   * @group Accessibility
   */
  @property({ type: String, attribute: 'aria-haspopup' })
  override ariaHasPopup: string | null = null

  /**
   * Identifies the element that describes the current element.
   * @group Accessibility
   */
  @property({ type: String, attribute: 'aria-describedby' })
  ariaDescribedBy: string | null = null

  /**
   * Indicates the current "pressed" state of toggle buttons.
   * @type { 'true' | 'false' | 'mixed' }
   * @group Accessibility
   */
  @property({ type: String, attribute: 'aria-pressed' })
  override ariaPressed: string | null = null

  /**
   * Indicates whether the element is hidden from accessibility APIs.
   * @type { 'true' | 'false' }
   * @group Accessibility
   */
  @property({ type: String, attribute: 'aria-hidden' })
  override ariaHidden: string | null = null

  /**
   * Custom CSS text to be merged with the component's styles.
   * This CSS will be injected into the shadow DOM and merged with the component's static styles.
   * @example
   * ```html
   * <dap-ds-spinner custom-styles=".spinner { border: 2px solid red; }"></dap-ds-spinner>
   * ```
   */
  @property({ type: String, attribute: 'custom-styles' })
  customStyles?: string

  private _customStyleSheet?: CSSStyleSheet

  emit<V = Record<PropertyKey, any>>(
    name: string,
    value?: V,
    options?: EventOptions,
  ) {
    const event = new CustomEvent<V>(name, {
      bubbles: true,
      cancelable: false,
      composed: true,
      detail: value ? ({ ...value } as V) : undefined,
      ...options,
    })

    this.dispatchEvent(event)

    return event
  }

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.firstUpdated(_changedProperties)
    this._updateCustomStyles()
  }

  protected updated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.updated(_changedProperties)
    // Update custom styles when customStyles changes
    if (_changedProperties.has('customStyles')) {
      this._updateCustomStyles()
    }
  }

  private _updateCustomStyles(): void {
    if (!this.shadowRoot) return

    if (this.customStyles) {
      // Create or update custom stylesheet using adoptedStyleSheets API
      // This ensures custom styles come after static styles and can override them
      if (this._customStyleSheet) {
        this._customStyleSheet.replaceSync(this.customStyles)
      } else {
        this._customStyleSheet = new CSSStyleSheet()
        this._customStyleSheet.replaceSync(this.customStyles)
        this.shadowRoot.adoptedStyleSheets = [
          ...this.shadowRoot.adoptedStyleSheets,
          this._customStyleSheet,
        ]
      }
    } else if (this._customStyleSheet) {
      // Remove custom stylesheet if not needed
      this.shadowRoot.adoptedStyleSheets =
        this.shadowRoot.adoptedStyleSheets.filter(
          sheet => sheet !== this._customStyleSheet,
        )
      this._customStyleSheet = undefined
    }
  }
}
